import {Component, OnInit, ViewChild} from '@angular/core';
import {Collection} from '../../../Models/Collection';
import {FormlyFieldConfig, FormlyFormOptions} from '@ngx-formly/core';
import {UntypedFormGroup} from '@angular/forms';
import {environment} from '../../../../../environments/environment';
import {TranslateService} from '@ngx-translate/core';
import {Helper} from '../../../../helpers/helper.service';
import {CollectionService} from '../../../Services/collection.service';
import {ActivatedRoute, Router} from '@angular/router';
import {LanguageService} from '../../../../translation/Services/language.service';
import {TranslationFormlyService, TranslationIdMap} from '../../../Services/translation.formly.service';
import {LanguageModel} from '../../../../translation/translation.models';
import {Property} from '../../../Models/Property';
import {OrderDownlineTreeviewEventParser, TreeviewConfig, TreeviewEventParser, TreeviewItem} from '@paymate-software/ngx-treeview';
import {ProductTreeviewConfig} from '../../supplier/view-supplier/view-supplier.component';
import {Order} from '../../../Models/Order';

export interface AttachValues {
  fabric: number;
  collection: number;
  checked: boolean;
}

@Component({
  selector: 'app-manage-collection',
  templateUrl: './manage-collection.component.html',
  styleUrls: ['./manage-collection.component.scss'],
  providers: [
    {provide: TreeviewEventParser, useClass: OrderDownlineTreeviewEventParser},
    {provide: TreeviewConfig, useClass: ProductTreeviewConfig}
  ]
})
export class ManageCollectionComponent implements OnInit {
  public classicBox;
  public lastValue = {collection: new Collection()};
  public enableSave = true;
  public items = [];
  public childItem = [];
  public collection: Collection = new Collection();
  public fabrics: AttachValues[] = [];
  form = new UntypedFormGroup({});
  model: any = {};
  public translations = [];
  public collectionId: number;
  public isEdit = false;
  options: FormlyFormOptions = {};
  public languageMap = {};
  public languages: LanguageModel[] = [];
  // D14/D16/D17: parallel {field: {locale: id}} lookup, see TranslationFormlyService.
  private translationIds: TranslationIdMap = {};
  private translationFields = [
    {field: 'name', required: true },
    {field: 'modelLabel', required: false },
    {field: 'productLabel', required: false }
  ];
  public properties: Property[] = [];
  @ViewChild('classicForm', {static: false}) child;
  public selectedValues: any[];
  config = TreeviewConfig.create({
    hasAllCheckBox: false,
    hasFilter: true,
    hasCollapseExpand: false,
    maxHeight: 400
  });

  constructor(
    public helper: Helper,
    private languageService: LanguageService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private collectionService: CollectionService,
    private router: Router
  ) {
  }

  ngOnInit() {
    this.languageService.list().subscribe(lang_resp => {
      this.languages = lang_resp.languages;
      this.languages.forEach(lang => {
        this.languageMap[lang.locale] = lang.name;
      });
      this.route.params.subscribe(params => {
        new Promise((resolve) => {
          if (Helper.isDefined(params['id'])) {
            this.collectionId = params['id'];
            this.isEdit = true;
            this.collectionService.getCollectionItem(this.collectionId).subscribe(resp => {
              resolve(resp);
              this.collection.fabrics = resp.fabrics;
            });
          } else {
            resolve(new Collection());
          }
        }).then(collection => {
          this.lastValue = {collection: (<Collection>collection)};
          this.selectedValues = this.lastValue.collection.properties.map(x => x.id);
        }).then(() => {
          this.collectionService.getFabricCollectionWithFabrics().subscribe((res) => {
            const ids = [];
            this.collection.fabrics.forEach((fabric) => {
              ids.push(fabric.id);
            });
            res.data.forEach((fabricCollection) => {
              const children = [];
              fabricCollection.fabrics.forEach((fabric) => {
                // check if ids in the fabrics ids
                children.push({
                  text: fabric.name, value: fabric.id, checked: (this.collection.fabrics.findIndex(x => x.id === fabric.id) !== -1)
                });
                this.fabrics.push({
                  fabric: fabric.id,
                  collection: this.collectionId,
                  checked: (this.collection.fabrics.findIndex(x => x.id === fabric.id) !== -1)
                });
              });
              const collectionItem = new TreeviewItem({text: fabricCollection.name, value: fabricCollection.id, children: children});
              this.childItem.push(collectionItem);
            });
          });
          this.items = this.childItem;
        })
          .then(() => {
            this.collectionService.getProperties().subscribe((res) => {
              this.properties = res;
              this.lastValue.collection.properties.forEach(property => {
                const element = this.properties.find(x => x.id === property.id);
                if (element) {
                  element.checked = true;
                }
              });
            });
            this.translationIds = TranslationFormlyService.buildTranslationIdMap(this.lastValue.collection.translations);
            this.lastValue.collection.translations = TranslationFormlyService.prepareTranslationFields(
              this.translationFields.map(item => item.field),
              this.lastValue.collection.translations, this.languages);
          }).then(() => {
          this.classicBox = <FormlyFieldConfig>[
            {
              className: 'section-label',
              template: '<div><strong>' + this.translate.instant('admin.collection.show.collection_name') + '</strong></div><hr/>',
            },
          ];
          this.classicBox = <FormlyFieldConfig>[];

          Object.keys(this.lastValue.collection.translations).forEach((field) => {
            const row = {
              fieldGroupClassName: 'row',
              key: 'collection.translations',
              fieldGroup: []
            };

            Object.entries(this.lastValue.collection.translations[field]).forEach((t) => {
              row.fieldGroup.push(
                {
                  className: 'col-6 ',
                  type: 'input',
                  key: field + '[' + t[0] + ']',
                  templateOptions: {
                    label: this.translate.instant('doctrine.collection.' + field) + ' (' + this.languageMap[t[0]] + ')',
                    required: this.translationFields.some(item => item.field === field && item.required),
                    maxLength: environment.maxStringLength,
                  },
                  validation: {
                    messages: {
                      pattern: this.translate.instant('errors.invalid'),
                      required: this.translate.instant('errors.required'),
                      maxlength: (error, fieldItem: FormlyFieldConfig) => this.translate.instant('validators.max_message',
                        {limit: fieldItem.templateOptions.maxLength})
                    },
                  },
                }
              );
            });
            this.classicBox.push(row);
          });

          this.classicBox.push(
            {
              className: 'section-label',
              template: '<div><strong>' + this.translate.instant('admin.collection.show.other') + '</strong></div><hr/>',
            },
            {
              fieldGroupClassName: 'row',
              key: 'collection',
              fieldGroup: [
                {
                  key: 'status',
                  type: 'ng-select',
                  className: 'col-3',
                  defaultValue: true,
                  templateOptions: {
                    label: this.translate.instant('doctrine.collection.status'),
                    required: true,
                    options: [
                      {label: this.translate.instant('global.active'), value: true},
                      {label: this.translate.instant('global.inactive'), value: false},
                    ],
                  },
                },
                {
                  key: 'type',
                  type: 'ng-select',
                  className: 'col-3',
                  defaultValue: true,
                  templateOptions: {
                    label: this.translate.instant('doctrine.collection.type'),
                    required: true,
                    options: [
                      {label: this.translate.instant('doctrine.collection.types.production'), value: Collection.TYPE_PRODUCTION},
                      {label: this.translate.instant('doctrine.collection.types.stock'), value: Collection.TYPE_STOCK},
                    ],
                  },
                },
              ],
            },
            {
              fieldGroupClassName: 'row mt-5',
              key: 'collection',
              fieldGroup: [
                {
                  key: 'endCustomerDelivery',
                  type: 'checkbox',
                  className: 'col-3 pt-4',
                  templateOptions: {
                    label: this.translate.instant('doctrine.collection.end_customer_delivery'),
                    required: false
                  },
                },
                {
                  className: 'col-3',
                  type: 'input',
                  key: 'deliveryCostPerArticle',
                  hideExpression: (model) => !model.endCustomerDelivery,
                  templateOptions: {
                    label: this.translate.instant('doctrine.collection.delivery_cost_per_article'),
                    required: false,
                    addonLeft: {
                      text: this.translate.instant('global.currency_sign'),
                    },
                    pattern: '^\\d{0,8}\\.\\d{1,4}$|^\\d{0,11}$',
                    maxLength: environment.maxStringLength,
                  },
                  validation: {
                    messages: {
                      pattern: this.translate.instant('errors.invalid'),
                      required: this.translate.instant('errors.required'),
                      maxlength: (error, field: FormlyFieldConfig) =>
                        this.translate.instant('validators.max_message', {limit: field.templateOptions.maxLength})
                    },
                  },
                },
                {
                  className: 'col-3',
                  type: 'input',
                  key: 'dealerDeliveryCostPerArticle',
                  hideExpression: (model) => !model.endCustomerDelivery,
                  templateOptions: {
                    label: this.translate.instant('doctrine.collection.dealer_delivery_cost_per_article'),
                    required: false,
                    addonLeft: {
                      text: this.translate.instant('global.currency_sign'),
                    },
                    pattern: '^\\d{0,8}\\.\\d{1,4}$|^\\d{0,11}$',
                    maxLength: environment.maxStringLength,
                  },
                  validation: {
                    messages: {
                      pattern: this.translate.instant('errors.invalid'),
                      required: this.translate.instant('errors.required'),
                      maxlength: (error, field: FormlyFieldConfig) =>
                        this.translate.instant('validators.max_message', {limit: field.templateOptions.maxLength})
                    },
                  },
                },
              ],
            },
            {
              fieldGroupClassName: 'row',
              key: 'collection',
              fieldGroup: [
                {
                  key: 'separateDeliveryAddress',
                  type: 'checkbox',
                  className: 'col-3 pt-4',
                  templateOptions: {
                    label: this.translate.instant('doctrine.collection.separate_delivery_address'),
                    required: false
                  },
                },
                {
                  key: 'primaryDeliveryAddress',
                  type: 'ng-select',
                  className: 'col-3',
                  defaultValue: true,
                  hideExpression: (model) => !model.separateDeliveryAddress,
                  templateOptions: {
                    label: this.translate.instant('doctrine.collection.default_address'),
                    required: true,
                    options: [
                      {label: this.translate.instant('doctrine.delivery_contact.use_default_address'), value: true},
                      {label: this.translate.instant('doctrine.delivery_contact.use_second_address'), value: false},
                    ],
                  },
                },
              ],
            },
          );
        });
      });
    });
  }
  enableButton(event) {
    this.enableSave = event;
  }

  submitItem(next: boolean) {
    if (next) {
      const collection = JSON.parse(JSON.stringify(this.child.onFormSubmit()));
      delete collection.collection.id;
      collection.collection.properties = this.selectedValues;
      const fabrics = [];
      this.fabrics.forEach((fabric) => {
        if (fabric.checked) {
          fabrics.push(fabric.fabric);
        }
      });
      collection.collection.fabrics = fabrics;
      collection.collection.translations = TranslationFormlyService.reorganizeTranslations(
        this.translationFields.map(item => item.field),
        collection.collection.translations,
        this.translationIds
      );
      this.collectionService.manageCollection(collection, this.isEdit, this.collectionId).subscribe(() => {
        this.router.navigateByUrl('/back-office/collection/overview');
      });
    } else {
      this.router.navigateByUrl('/back-office/collection/overview');
    }
  }


  onSelectedChange(items) {
    this.fabrics.map((fabric) => fabric.checked = false);
    items.forEach(downlineItem => {
      const item = downlineItem.item;
      const value = item.value;
      const fabricIndexWithId = this.fabrics.findIndex(x => x.fabric === value);
      if (!item.disabled && fabricIndexWithId >= 0) {
        this.fabrics[fabricIndexWithId].checked = true;
      }
    });
  }

  public onToggle() {
    const checkedProperties = this.properties.filter(x => x.checked);
    this.selectedValues = checkedProperties.map(x => x.id);
  }

  getProperty() {
    return Property;
  }

}
